// src/pages/Dashboard/components/DashboardSidebar.jsx
import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { formatWalletAddress } from '../../../utils/formatters';
import '../styles/dashboardSidebar.css';

/**
 * Dashboard Sidebar Component
 * Provides navigation for the dashboard workbench
 */
const DashboardSidebar = ({ selectedNode, activatedNodes, networkStats, wallet }) => {
  const location = useLocation();
  
  // Count nodes by tier
  const getNodeCountsByTier = () => {
    const counts = {
      apex: 0,
      master: 0,
      super: 0,
      standard: 0,
      total: activatedNodes.length
    };
    
    activatedNodes.forEach(node => {
      const tierName = (node.tier_name || node.tier || '').toLowerCase();
      if (tierName.includes('apex')) counts.apex++;
      else if (tierName.includes('master')) counts.master++;
      else if (tierName.includes('super')) counts.super++;
      else counts.standard++;
    });
    
    return counts;
  };
  
  const nodeCounts = getNodeCountsByTier();
  
  // Format total commissions or return '-'
  const formattedTotalCommissions = networkStats?.total_commissions 
    ? Number(networkStats.total_commissions).toLocaleString('en-US', { maximumFractionDigits: 2 })
    : '-';
  
  return (
    <aside className="dashboard-sidebar">
      <div className="sidebar-header">
        <div className="logo">
          <h3>Node Dashboard</h3>
        </div>
      </div>
      
      <div className="sidebar-user">
        <div className="user-wallet">
          <div className="wallet-icon">
            {wallet.type === 'metamask' ? (
              <svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M27.5933 3L17.6 11.1867L19.4133 6.4L27.5933 3Z" fill="#E17726"/>
                <path d="M4.4 3L14.3067 11.28L12.5867 6.4L4.4 3Z" fill="#E27625"/>
                <path d="M23.8933 21.6133L21.0667 25.9867L26.96 27.6267L28.6667 21.7067L23.8933 21.6133Z" fill="#E27625"/>
                <path d="M3.34666 21.7067L5.04 27.6267L10.9333 25.9867L8.10666 21.6133L3.34666 21.7067Z" fill="#E27625"/>
              </svg>
            ) : wallet.type === 'phantom' ? (
              <svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="32" height="32" rx="6" fill="#551BF9"/>
                <path d="M23 10H15C12.2 10 10 12.2 10 15C10 17.8 12.2 20 15 20H17V22H21V16H15C14.4 16 14 15.6 14 15C14 14.4 14.4 14 15 14H23V10Z" fill="white"/>
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="32" height="32" rx="6" fill="#6366f1"/>
                <path d="M8 16C8 16.5523 7.55228 17 7 17C6.44772 17 6 16.5523 6 16C6 15.4477 6.44772 15 7 15C7.55228 15 8 15.4477 8 16Z" fill="white"/>
                <path d="M17 16C17 16.5523 16.5523 17 16 17C15.4477 17 15 16.5523 15 16C15 15.4477 15.4477 15 16 15C16.5523 15 17 15.4477 17 16Z" fill="white"/>
                <path d="M26 16C26 16.5523 25.5523 17 25 17C24.4477 17 24 16.5523 24 16C24 15.4477 24.4477 15 25 15C25.5523 15 26 15.4477 26 16Z" fill="white"/>
              </svg>
            )}
          </div>
          <div className="wallet-address">
            {formatWalletAddress(wallet.address)}
          </div>
          <div className={`network-badge ${wallet.network}`}>
            {wallet.network === 'ethereum' ? 'ETH' : 
             wallet.network === 'bsc' ? 'BSC' : 
             wallet.network === 'solana' ? 'SOL' : wallet.network}
          </div>
        </div>
      </div>
      
      <nav className="sidebar-nav">
        <NavLink to="/dashboard" className={({ isActive }) => `nav-item ${isActive && location.pathname === '/dashboard' ? 'active' : ''}`}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M9 22V12H15V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>Dashboard</span>
        </NavLink>
        
        <NavLink to="/dashboard/nodes" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M17 21V19C17 16.7909 15.2091 15 13 15H5C2.79086 15 1 16.7909 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M23 21V19C22.9986 17.1771 21.765 15.5857 20 15.13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M16 3.13C17.7699 3.58317 19.0078 5.17799 19.0078 7.005C19.0078 8.83201 17.7699 10.4268 16 10.88" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>Node Management</span>
          <span className="nav-counter">{nodeCounts.total}</span>
        </NavLink>
        
        <NavLink to="/dashboard/invitations" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <rect x="9" y="3" width="6" height="4" rx="1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M9 12H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M9 16H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>Invitations</span>
          {selectedNode && <span className="nav-counter">{selectedNode.referral_count || 0}</span>}
        </NavLink>
        
        <NavLink to="/dashboard/network" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M16 21V19C16 17.9391 15.5786 16.9217 14.8284 16.1716C14.0783 15.4214 13.0609 15 12 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M8.5 11C10.7091 11 12.5 9.20914 12.5 7C12.5 4.79086 10.7091 3 8.5 3C6.29086 3 4.5 4.79086 4.5 7C4.5 9.20914 6.29086 11 8.5 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>Team Network</span>
          {networkStats && <span className="nav-counter">{networkStats.total_network_size || 0}</span>}
        </NavLink>
      </nav>
      
      <div className="sidebar-stats">
        <div className="stats-heading">Node Statistics</div>
        
        <div className="stats-grid">
          <div className="stats-item">
            <div className="stats-label">Total Nodes</div>
            <div className="stats-value">{nodeCounts.total}</div>
          </div>
          
          <div className="stats-item">
            <div className="stats-label">Apex Nodes</div>
            <div className="stats-value">{nodeCounts.apex}</div>
          </div>
          
          <div className="stats-item">
            <div className="stats-label">Master Nodes</div>
            <div className="stats-value">{nodeCounts.master}</div>
          </div>
          
          <div className="stats-item">
            <div className="stats-label">Super Nodes</div>
            <div className="stats-value">{nodeCounts.super}</div>
          </div>
          
          <div className="stats-item">
            <div className="stats-label">Standard Nodes</div>
            <div className="stats-value">{nodeCounts.standard}</div>
          </div>
          
          <div className="stats-item total-commissions">
            <div className="stats-label">Total Commissions</div>
            <div className="stats-value">{formattedTotalCommissions}</div>
          </div>
        </div>
      </div>
      
      <div className="sidebar-footer">
        <a href="/help" className="help-link">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <path d="M9 10C9 8.89543 9.89543 8 11 8H12C13.1046 8 14 8.89543 14 10C14 11.1046 13.1046 12 12 12H12V14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <path d="M12 17L12 17.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <span>Need Help?</span>
        </a>
      </div>
    </aside>
  );
};

export default DashboardSidebar;
